/**
 * The File Snap table (BM-H4295, shared by BM-H4308).
 *
 * One format for the all-matters File Snap page and the matter page card.
 * Vanessa, 14 Oct 2026: "can we use same format as the all-in page?" and, for
 * the matter card, "we won't need the matter column".
 *
 * Icon for type, date, (matter), summary with the full note on click, who,
 * and the row's actions. Test ids are passed in so each screen keeps its own.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { FileSnapTypeIcon, snapSummary, AiMark } from "./fileSnapRow";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function FileSnapTable({
  rows,
  showMatter = false,
  whoOf = (r) => r.fee_earner || r.fee_earner_name || "",
  actions = null,
  rowTestId = (r) => `file-snap-row-${r.id}`,
  textTestId = (r) => `file-snap-summary-${r.id}`,
  dimTouchpoints = false,
}) {
  const [openId, setOpenId] = useState(null);
  return (
    <table className="w-full table-fixed text-left text-[15px] leading-snug" data-testid="file-snap-table">
      <colgroup>
        <col className="w-9" />
        <col className="w-32" />
        {showMatter && <col className="w-40" />}
        <col />
        <col className="w-36" />
        <col className={actions ? "w-24" : "w-9"} />
      </colgroup>
      <thead>
        <tr className="border-b border-white/10 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <th className="py-2 pl-3 pr-1"><span className="sr-only">Type</span></th>
          <th className="py-2 px-2">Date</th>
          {showMatter && <th className="py-2 px-2">Matter</th>}
          <th className="py-2 px-2">Summary</th>
          <th className="py-2 px-2">{showMatter ? "Fee Earner" : "By"}</th>
          <th className="py-2 px-2"><span className="sr-only">Open</span></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const open = openId === r.id;
          const summary = snapSummary(r);
          const full = (r.note_text || "").trim();
          const who = whoOf(r);
          const faint = dimTouchpoints && r.type === "touchpoint";
          return (
            <tr
              key={r.id}
              className={`border-b border-white/5 hover:bg-white/[0.03] cursor-pointer align-top ${open ? "bg-white/[0.03]" : ""}`}
              onClick={() => setOpenId(open ? null : r.id)}
              aria-expanded={open}
              data-testid={rowTestId(r)}
            >
              <td className="py-2.5 pl-3 pr-1"><FileSnapTypeIcon row={r} /></td>
              <td className="py-2.5 px-2 text-xs text-slate-300">
                {fmtDate(r.date)}
                {r.duration_minutes > 0 && <div className="text-slate-500">{r.duration_minutes}m</div>}
              </td>
              {showMatter && (
                <td className="py-2.5 px-2 truncate" onClick={(e) => e.stopPropagation()}>
                  {r.matter_ref ? (
                    <Link to={`/matter/${encodeURIComponent(r.matter_ref)}`}
                          className="font-medium text-teal hover:underline"
                          data-testid={`file-snap-matter-link-${r.id}`}>
                      {r.matter_ref}
                    </Link>
                  ) : <span className="text-slate-500">—</span>}
                  {r.client_name && <div className="text-xs text-txt-muted truncate" title={r.client_name}>{r.client_name}</div>}
                </td>
              )}
              <td className={`py-2.5 px-2 ${faint ? "text-slate-300" : "text-slate-100"}`} data-testid={textTestId(r)}>
                {open ? (
                  <div className="whitespace-pre-wrap">{full || summary || <span className="text-slate-500">No text recorded</span>}</div>
                ) : (
                  <div className="line-clamp-2">
                    <AiMark row={r} />
                    {/* A touchpoint's text already names its party; a note's does not. */}
                    {r.party_name && r.type !== "touchpoint" && <span className="text-slate-400">{r.party_name}: </span>}
                    {summary || <span className="text-slate-500">No text recorded</span>}
                  </div>
                )}
              </td>
              <td className="py-2.5 px-2 text-xs text-slate-300 truncate" title={who}>{who || "—"}</td>
              <td className="py-2.5 px-2" onClick={(e) => e.stopPropagation()}>
                {actions ? actions(r) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
